"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sessionStatus = void 0;
const env_1 = require("./utils/env");
const event_agent_1 = require("./utils/event.agent");
const SESSION_MONITOR_ENDPOINT = '/rest/v1/session/monitor';
const SESSION_MONITOR_PARAM = 'sessionActive';
const SESSION_MONITOR_STORAGE = 'AUTH_SESSION_ACTIVE';
const LOGOUT_PAYLOAD_STORAGE = 'AUTH_LOGOUT_PAYLOAD';
var sessionStatus;
(function (sessionStatus) {
    sessionStatus["INVALID"] = "INVALID";
    sessionStatus["REDIRECT"] = "REDIRECT";
    sessionStatus["VALID"] = "VALID";
})(sessionStatus = exports.sessionStatus || (exports.sessionStatus = {}));
/** Implements the current user session validation flow. */
class SessionMonitor {
    /**
     * Create a session monitor.
     * @property authService - OIDC AuthService instance.
     * @property eventTracker - Event Tracker instance.
     */
    constructor(authService, eventTracker) {
        this.authService = authService;
        this.eventTracker = eventTracker;
        this.baseUrl = this.getBaseUrl();
    }
    /**
     * Validation completion handler for validate method.
     */
    callback() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendLogoutEvent();
            sessionStorage.removeItem(SESSION_MONITOR_STORAGE);
            this.removeParam();
        });
    }
    /**
     * Get id token from the current user session.
     */
    getToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.authService.getUser();
            return user === null || user === void 0 ? void 0 : user.id_token;
        });
    }
    /**
     * Get session monitor URL.
     * @property token - current id token.
     * @returns Session monitor URL.
     */
    getUrl(token) {
        const params = new URLSearchParams(Object.assign({ id_token_hint: token }, (env_1.isCustomDomain && { post_revalidate_redirect_uri: window.location.href })));
        return `${this.baseUrl}?${params}`;
    }
    /**
     * @description Gets the base URL for session monitor requests.
     */
    getBaseUrl() {
        var _a;
        const isSurrogate = (_a = this.authService.getAuthority()) === null || _a === void 0 ? void 0 : _a.includes('surrogate');
        const DOMAIN = isSurrogate ? 'SURROGATE' : 'SSO';
        return env_1.SSO_DOMAINS[`${DOMAIN}_${env_1.ENV}`] + SESSION_MONITOR_ENDPOINT;
    }
    /**
     * Remove control params from URL.
     */
    removeParam() {
        const url = new URL(location.href);
        url.searchParams.delete(SESSION_MONITOR_PARAM);
        history.replaceState({}, '', url.href);
    }
    /**
     * Send a logout event from event tracker.
     */
    sendLogoutEvent() {
        try {
            const payload = sessionStorage.getItem(LOGOUT_PAYLOAD_STORAGE);
            if (!payload)
                return;
            const _a = JSON.parse(payload), { user } = _a, data = __rest(_a, ["user"]);
            this.eventTracker.send(event_agent_1.EVENT_ENTITY.LOGOUT, data, user);
            sessionStorage.removeItem(LOGOUT_PAYLOAD_STORAGE);
        }
        catch (e) {
            console.error('[sendLogoutEvent]', e);
        }
    }
    /**
     * Validate the current session by redirect flow.
     * @property token - current id token
     */
    validateRedirect(token) {
        window.location.href = this.getUrl(token);
    }
    /**
     * Validate the current session by silent flow.
     * @property token - current id token.
     * @throws Session not found on server.
     * @returns VALID, if the current token is valid or
     * INVALID otherwise.
     */
    validateSilent(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(this.getUrl(token), {
                method: 'GET',
                mode: 'cors',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (response.status === 404) {
                return sessionStatus.INVALID;
            }
            return sessionStatus.VALID;
        });
    }
    /**
     * Check whether the current URL is a response route or not.
     * @returns True for a response route or false otherwise.
     */
    isResponseRoute() {
        const url = new URL(location.href);
        return url.searchParams.has(SESSION_MONITOR_PARAM);
    }
    /**
     * The response handler for session monitor validation.
     */
    response() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = new URL(location.href);
            const isValid = url.searchParams.get(SESSION_MONITOR_PARAM) === 'true';
            this.removeParam();
            sessionStorage.setItem(SESSION_MONITOR_STORAGE, url.searchParams.get(SESSION_MONITOR_PARAM));
            if (!isValid)
                return localStorage.clear();
            sessionStorage.removeItem(LOGOUT_PAYLOAD_STORAGE);
        });
    }
    /**
     * Save the logout event payload in session storage.
     * @param payload - the logout event payload.
     */
    setLogoutPayload(payload) {
        sessionStorage.setItem(LOGOUT_PAYLOAD_STORAGE, JSON.stringify(payload));
    }
    /**
     * Validate the current session.
     * @param isPrivateRoute - whether the current route is private or not.
     * @returns A promise resolution containing the following status:
     * REDIRECT - validation flow started by redirect.
     * VALID - valid session.
     * INVALID - invalid session.
     */
    validate(isPrivateRoute) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isResponseRoute()) {
                const isValid = !!sessionStorage.getItem(SESSION_MONITOR_STORAGE);
                yield this.callback();
                return isValid;
            }
            try {
                const token = yield this.getToken();
                if (!token)
                    return Promise.resolve(sessionStatus.INVALID);
                if (env_1.isCustomDomain) {
                    this.validateRedirect(token);
                    return Promise.resolve(sessionStatus.REDIRECT);
                }
                return yield this.validateSilent(token);
            }
            catch (e) {
                console.error('[CAS][SessionMonitor]', e);
                return Promise.resolve(isPrivateRoute ? sessionStatus.INVALID : sessionStatus.VALID);
            }
        });
    }
}
exports.default = SessionMonitor;



//////////////////
// WEBPACK FOOTER
// ./~/@hotmart/cas-js/dist/session.monitor.js
// module id = 729
// module chunks = 0